<template>
  <PreGameModal :progress="progress" :progressTotal="progressTotal">
    <template #content>
      <PreGameModalWaitingError v-if="error !== null" @back="$emit('back')">
        <template #text v-if="error === Error.TIMEOUT">
          <b>Vous avez dépassé le délai de 10 minutes</b> pour commencer à jouer. Veuillez cliquer sur le bouton
          ci-dessous pour relancer une session.
        </template>
        <template #text v-else>
          Un <b>problème de chargement</b> est survenu. Veuillez cliquer sur le bouton ci-dessous pour relancer une
          session.
        </template>
      </PreGameModalWaitingError>
      <PreGameModalWaitingReady @go="go" v-else-if="cloudClient.pixelStreamingURL && !showIframe" />
      <PreGameModalWaitingProgress :loadingProgress="loadingProgress" v-else-if="!showIframe" />
    </template>
  </PreGameModal>
  <div v-if="iFrameEnabled && cloudClient.isV2() && showIframe" class="fullscreen-iframe">
    <iframe
      :src="cloudClient.pixelStreamingURL"
      frameborder="0"
      allowfullscreen
      style="width: 100vw; height: 100vh; border: none;"
    ></iframe>
    <button class="close-btn" @click="closeIframe">✕</button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';

import { useCloudClientStore } from "@/stores/cloud-client";

import PreGameModal from './PreGameModal.vue';
import PreGameModalWaitingProgress from './waiting/partials/PreGameModalWaitingProgress.vue';
import PreGameModalWaitingReady from './waiting/partials/PreGameModalWaitingReady.vue';
import PreGameModalWaitingError from './waiting/partials/PreGameModalWaitingError.vue';

defineProps({
  progress: {
    type: Number,
    required: true,
  },
  progressTotal: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits([
  'back',
]);

enum Error {
  TIMEOUT,
  OTHER,
}

const iFrameEnabled = import.meta.env.VITE_I_FRAME_ENABLED === 'true';

const cloudClient = useCloudClientStore();

const time = ref(0);
const timeToWait = ref(150000);

const loadingProgress = computed(() => (time.value * 100) / timeToWait.value);
const error = ref<Error | null>(null);

const showIframe = ref(false);

onMounted(() => {
  cloudClient.startCloudSession();
  const timer = setInterval(() => {
    time.value = time.value + 100;

    if (time.value === timeToWait.value) {
      clearInterval(timer);
    }
  }, 100);

  cloudClient.emitter.on("onCapacityError", () => {
    error.value = Error.OTHER;
  });
});

let timeout: NodeJS.Timeout | null = null;

watch(cloudClient, (newCloudClient) => {
  if (newCloudClient.pixelStreamingURL) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      error.value = Error.TIMEOUT;
    }, 1000 * 60 * 10); // 10 minutes
  }
});

function waitForGameWindowClosed(window, cb) {
  function poll() {
    if (window.closed) cb();
    else requestAnimationFrame(poll);
  }
  poll();
}

function go() {
  if (timeout) clearTimeout(timeout);
  
  if (iFrameEnabled && cloudClient.isV2()) {
    cloudClient.pixelStreamingURL = cloudClient.pixelStreamingURL.replace("http://", "https://");
    showIframe.value = true;
  } else {
    const gameWindow = window.open(cloudClient.pixelStreamingURL, '_blank');

    if (gameWindow) {
      waitForGameWindowClosed(gameWindow, () => {
        window.location.reload();
      });
    }
  }
  
}

function closeIframe() {
  showIframe.value = false;
  window.location.reload();
}
</script>

<style lang="scss" scoped>
.fullscreen-iframe {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.close-btn {
  position: absolute;
  top: 16px;
  left: 24px;
  z-index: 10000;
  background: rgba(0,0,0,0.7);
  color: #fff;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 50%;
}
</style>